/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import SocialAnalytics from './Social.analytics';
import { SocialActions, CATEGORY, LINE_OF_BUSINESS } from './Social.meta';
import { SCREEN_NAME } from '../constants';
import type { Post } from '../../services/types';

const mapEvent = (
  actionKey: (typeof SocialActions)[keyof typeof SocialActions],
  params: { payload: { params: { label: string } } },
) => {
  const handler = SocialAnalytics[actionKey];
  return typeof handler === 'function' ? handler(params) : undefined;
};

export interface TrackFeedItemClickParams {
  post: Post;
  currency?: string;
  index?: number;
}

/**
 * Track FeedItemView click (video/image tile tap)
 * Returns mapped params for use with useLogger().logEvent()
 */
export const trackFeedItemClick = ({
  post,
  currency = '',
  index,
}: TrackFeedItemClickParams) => ({
  category: CATEGORY.movements,
  lineofbusiness: LINE_OF_BUSINESS.socialUgc,
  screenName: SCREEN_NAME.homeV2,
  screen_name: SCREEN_NAME.homeV2,
  productid: post.post_id,
  productname: post.snippet || '',
  productcategory: post.media?.type || '',
  strikethroughprice: '',
  price: '',
  currency,
  position: index,
});

/**
 * Track FeedHeader tap (section header with arrow navigation)
 * Returns mapped params for use with useLogger().logEvent(SOCIAL_FEED_EVENT, ...)
 */
export const trackFeedHeaderTap = (sectionId: string, labelType: string) => {
  const label = `${sectionId}|${labelType}`;
  return mapEvent(SocialActions.Header_Tap, { payload: { params: { label } } });
};

/**
 * Track Footer chip tap (destination chip in feed footer)
 * Returns mapped params for use with useLogger().logEvent(SOCIAL_FEED_EVENT, ...)
 */
export const trackFooterChipTap = (chipId: string, labelType: string) => {
  const label = `${chipId}|${labelType}`;
  return mapEvent(SocialActions.Footer_Chip_Tap, {
    payload: { params: { label } },
  });
};
